import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import { DEFAULT_FEED_OUTPUT, type FeedOutput, type Store } from '../api/types';
import { AppHeader } from '../components/AppHeader';
import FeedProductSelection from '../components/FeedProductSelection';
import FeedSettingsForm from '../components/FeedSettingsForm';
import FeedStatusCard from '../components/FeedStatusCard';
import { pathError } from '../lib/feedOutput';
import { useAuth } from '../store/auth';
import { useStoreSlice } from '../store/storeRegistry';

const same = (a: FeedOutput, b: FeedOutput) => JSON.stringify(a) === JSON.stringify(b);

export default function FeedsTab() {
  const { selectedStoreId } = useStoreSlice();
  const qc = useQueryClient();
  const me = useAuth((s) => s.me);
  const storesQ = useQuery({ queryKey: ['stores'], queryFn: api.listStores });
  const store: Store | null = useMemo(
    () => storesQ.data?.find((s) => s.id === selectedStoreId) ?? null, [storesQ.data, selectedStoreId]);
  const saved: FeedOutput = store?.feed_output ?? DEFAULT_FEED_OUTPUT;
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState<FeedOutput>(saved);
  // Re-seed on store switch and after every save/refetch of the saved record.
  useEffect(() => { setDraft(JSON.parse(savedKey)); }, [store?.id, savedKey]);
  const dirty = !same(draft, saved);
  const isOwner = !!me && !!store && (me.role === 'admin' ||
    (!!store.org_id && me.org?.id === store.org_id && me.org_role === 'owner'));
  const canEdit = isOwner;
  const statusQ = useQuery({
    queryKey: ['feed-status', store?.id, 10],
    queryFn: () => api.feedStatus(store!.id, 10),
    enabled: !!store,
  });
  const save = useMutation({
    mutationFn: () => api.updateStore(store!.id, { ...store!, feed_output: draft }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['feed-status', store!.id] });
    },
  });
  const saveError = save.error instanceof HttpError ? save.error.detail : save.error ? (save.error as Error).message : null;
  const pathErr = store ? pathError(draft.path ?? `${store.id}/feed.xml`) : null;
  const onChange = (p: Partial<FeedOutput>) => { if (canEdit) setDraft((v) => ({ ...v, ...p })); };

  return (
    <div className="min-h-screen bg-ff-ivory relative" data-testid="feeds-page">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />
      <AppHeader active="feeds" />
      <main className="relative mx-auto max-w-[1240px] px-5 pb-32 pt-10 animate-rise sm:px-8 sm:pt-12">
        <div className="mb-10 border-b border-ff-line pb-10">
          <p className="ff-kicker mb-4">Feed output</p>
          <h1 className="ff-page-title text-[clamp(3.25rem,7vw,5.25rem)] leading-[0.92]">Feeds</h1>
          <p className="mt-4 max-w-2xl text-ff-mute">
            Your product feed, republished with the rendered creatives. Point Meta and Google at the feed URL.
          </p>
        </div>
        {!store ? (
          <p className="text-ff-mute" data-testid="feeds-empty">
            {storesQ.isLoading ? 'Loading stores…' : 'Select a store in the header to manage its feed.'}
          </p>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div className="space-y-6">
              <FeedSettingsForm store={store} value={draft} onChange={onChange} status={statusQ.data} canEdit={canEdit} />
              {draft.enabled && (
                <FeedProductSelection value={draft} onChange={onChange} status={statusQ.data} canEdit={canEdit} />
              )}
            </div>
            <div className="lg:sticky lg:top-24 self-start">
              <FeedStatusCard store={store} status={statusQ.data} isOwner={isOwner} dirty={dirty} />
            </div>
          </div>
        )}
      </main>
      {store && canEdit && dirty && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ff-line bg-ff-paper/95 backdrop-blur" data-testid="feeds-save-bar">
          <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-5 py-3 sm:px-8">
            <span className="text-sm text-ff-mute">Unsaved changes{saveError ? ` · ${saveError}` : ''}</span>
            <div className="flex gap-2">
              <button type="button" className="ff-btn-ghost" data-testid="feeds-discard"
                onClick={() => setDraft(JSON.parse(savedKey))}>Discard</button>
              <button type="button" className="ff-btn-primary disabled:opacity-40" data-testid="feeds-save"
                disabled={save.isPending || !!pathErr} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
